import React, { useEffect, useState, useMemo } from 'react';
import logsService, { fetchCommandQueue } from '../services/logsService';
import { useI18n } from '../i18n/I18nProvider';
import authService from '../services/authService';
import Modal from '../components/Modal';
import binService from '../services/binService';
import { startRealtime, on as onRealtime } from '../services/realtime';

const columns = ['created_at','action','entity_type','entity_id','user_id'];

function formatDate(ts){
  try { return new Date(ts).toLocaleString(); } catch(e){ return ts; }
}

const LogsPage = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('bin');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null); // selected log entry
  const [showRaw, setShowRaw] = useState(false);
  // Filters
  const [fromTs, setFromTs] = useState('');
  const [toTs, setToTs] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const [binIdMap, setBinIdMap] = useState(new Map()); // bin_id -> bin_identifier

  // Command queue tab state
  const [cmdQueueStatus, setCmdQueueStatus] = useState('all');
  const [cmdQueueRows, setCmdQueueRows] = useState([]);
  const [cmdQueueLoading, setCmdQueueLoading] = useState(false);
  const [cmdQueueError, setCmdQueueError] = useState('');
  const [cmdQueueRevision, setCmdQueueRevision] = useState(0);

  useEffect(() => {
    if (!isAdmin) return; // safety
    setLoading(true);
    setError('');
    logsService.fetchAudit({ entityType: activeTab, limit: 200 })
      .then(res => setRows(res.data))
      .catch(e => setError(e.response?.data?.msg || e.message || 'Error'))
      .finally(()=> setLoading(false));
  }, [activeTab, isAdmin]);

  // Realtime: capture media.ack events and surface immediately in logs
  useEffect(() => {
    if (!isAdmin) return;
    startRealtime();
    const offAck = onRealtime('media.ack', ev => {
      const synthetic = {
        id: 'rt-' + Date.now(),
        created_at: new Date().toISOString(),
        action: `media.ack.${ev.action || 'unknown'}`,
        entity_type: 'bin',
        entity_id: ev.bin_id,
        user_id: null,
        before: {},
        after: ev.payload || {}
      };
      // Prepend so it appears at the top; keep only recent window size
      setRows(prev => [synthetic, ...prev].slice(0, 400));
    });
    return () => { offAck(); };
  }, [isAdmin]);

  // Command queue loader
  useEffect(() => {
    if (!isAdmin || activeTab !== 'cmdqueue') return;
    setCmdQueueLoading(true);
    setCmdQueueError('');
    fetchCommandQueue({ status: cmdQueueStatus })
      .then(res => setCmdQueueRows(res.data))
      .catch(e => setCmdQueueError(e.response?.data?.msg || e.message || 'Error'))
      .finally(() => setCmdQueueLoading(false));
  }, [isAdmin, activeTab, cmdQueueStatus, cmdQueueRevision]);

  // Load bins when on bin tab to map IDs to user identifiers
  useEffect(() => {
    if (!isAdmin || activeTab !== 'bin') return;
    binService.getAllBins().then(data => {
      const m = new Map();
      (data||[]).forEach(b => {
        if (b && b.bin_id != null) m.set(b.bin_id, b.bin_identifier || String(b.bin_id));
      });
      setBinIdMap(m);
    }).catch(()=>{});
  }, [activeTab, isAdmin]);

  // Derived lists for filters
  const actionsList = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.action) set.add(r.action); });
    return Array.from(set).sort();
  }, [rows]);
  const entitiesList = useMemo(() => {
    const set = new Set();
    rows.forEach(r => {
      if (r.entity_id != null) {
        const raw = String(r.entity_id);
        const display = activeTab === 'bin' ? (binIdMap.get(r.entity_id) || raw) : raw;
        set.add(String(display));
      }
    });
    return Array.from(set).sort((a,b)=>{
      const na = Number(a), nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na-nb;
      return a.localeCompare(b);
    });
  }, [rows, activeTab, binIdMap]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      // time range
      if (fromTs) {
        const f = new Date(fromTs).getTime();
        const rt = new Date(r.created_at).getTime();
        if (!isNaN(f) && !isNaN(rt) && rt < f) return false;
      }
      if (toTs) {
        const t2 = new Date(toTs).getTime();
        const rt = new Date(r.created_at).getTime();
        if (!isNaN(t2) && !isNaN(rt) && rt > t2) return false;
      }
      // action
      if (actionFilter && r.action !== actionFilter) return false;
      // entity id
      if (entityFilter) {
        const raw = String(r.entity_id);
        const display = activeTab === 'bin' ? (binIdMap.get(r.entity_id) || raw) : raw;
        if (String(display) !== entityFilter) return false;
      }
      return true;
    });
  }, [rows, fromTs, toTs, actionFilter, entityFilter]);

  // compute changed keys list (shallow diff) when selected changes
  const diffInfo = useMemo(() => {
    if (!selected) return null;
    const before = selected.before || {};
    const after = selected.after || {};
    // gather keys
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
    const changes = [];
    keys.forEach(k => {
      const b = before[k];
      const a = after[k];
      const equal = JSON.stringify(b) === JSON.stringify(a);
      if (!equal) {
        changes.push({ key: k, before: b, after: a });
      }
    });
    return { changes, before, after };
  }, [selected]);

  return (
  <div className="sb-page">
      {!isAdmin && (
        <div style={{ padding: 16 }}>{t('logs.notAuthorized') || 'Not authorized'}</div>
      )}
      {isAdmin && <div className="tab-buttons">
        <button
          type="button"
          onClick={() => setActiveTab('bin')}
          className={`btn tab-button ${activeTab === 'bin' ? 'active' : ''}`}
        >{t('logs.tabBins') || 'Kukák'}</button>
        <button
          type="button"
          onClick={() => setActiveTab('site')}
          className={`btn tab-button ${activeTab === 'site' ? 'active' : ''}`}
        >{t('menu.sites') || 'Telephelyek'}</button>
        <button
          type="button"
          onClick={() => setActiveTab('media')}
          className={`btn tab-button ${activeTab === 'media' ? 'active' : ''}`}
        >{t('logs.tabMedia') || 'Fájlok'}</button>
        <button
          type="button"
          onClick={() => setActiveTab('user')}
          className={`btn tab-button ${activeTab === 'user' ? 'active' : ''}`}
        >{t('logs.tabUsers') || 'Felhasználók'}</button>
        <button
          type="button"
          onClick={() => setActiveTab('AppSettings')}
          className={`btn tab-button ${activeTab === 'AppSettings' ? 'active' : ''}`}
        >{t('menu.settings') || 'Beállítások'}</button>
        <button
          type="button"
          onClick={() => setActiveTab('cmdqueue')}
          className={`btn tab-button ${activeTab === 'cmdqueue' ? 'active' : ''}`}
        >Parancs sor</button>
      </div>}
      {isAdmin && loading && <div>{t('logs.loading') || 'Betöltés...'}</div>}
      {isAdmin && error && <div className="alert alert-danger">{error}</div>}
      {isAdmin && !loading && !error && (
        <div style={{ overflowX: 'auto' }}>
          {/* Filters row */}
          <div style={{ display:'flex', gap:12, alignItems:'flex-end', margin:'8px 0' }}>
            <div style={{ display:'flex', flexDirection:'column' }}>
              <label style={{ fontSize:11, fontWeight:600 }}>{t('logs.filterTimeFrom') || 'Kezdés'}</label>
              <input type="datetime-local" className="form-control" value={fromTs} onChange={e=> setFromTs(e.target.value)} />
            </div>
            <div style={{ display:'flex', flexDirection:'column' }}>
              <label style={{ fontSize:11, fontWeight:600 }}>{t('logs.filterTimeTo') || 'Vége'}</label>
              <input type="datetime-local" className="form-control" value={toTs} onChange={e=> setToTs(e.target.value)} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', minWidth:160 }}>
              <label style={{ fontSize:11, fontWeight:600 }}>{t('logs.action') || 'Akció'}</label>
              <select className="form-control" value={actionFilter} onChange={e=> setActionFilter(e.target.value)}>
                <option value="">{t('common.all') || 'Összes'}</option>
                {actionsList.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', flexDirection:'column', minWidth:160 }}>
              <label style={{ fontSize:11, fontWeight:600 }}>
                {activeTab === 'bin' ? (t('logs.entityIdBin') || 'Kuka ID') : activeTab === 'site' ? (t('logs.entityIdSite') || 'Telephely ID') : (t('logs.entityId') || 'Entitás ID')}
              </label>
              <select className="form-control" value={entityFilter} onChange={e=> setEntityFilter(e.target.value)}>
                <option value="">{t('common.all') || 'Összes'}</option>
                {entitiesList.map(id => <option key={id} value={id}>{id}</option>)}
              </select>
            </div>
            {(fromTs || toTs || actionFilter || entityFilter) && (
              <button type="button" className="btn btn-secondary" onClick={()=>{ setFromTs(''); setToTs(''); setActionFilter(''); setEntityFilter(''); }} style={{ height:32 }}>
                {t('common.clear') || 'Törlés'}
              </button>
            )}
          </div>
          <table className="table table-sm" style={{ minWidth: 800 }}>
            <thead>
              <tr>
                <th>{t('logs.time') || 'Idő'}</th>
                <th>{t('logs.action') || 'Akció'}</th>
                <th>{t('logs.entity') || 'Entitás'}</th>
                <th>{activeTab === 'bin' ? (t('logs.entityIdBin') || 'Kuka ID') : activeTab === 'site' ? (t('logs.entityIdSite') || 'Telephely ID') : activeTab === 'AppSettings' ? (t('logs.settingCol') || 'Beállítás') : (t('logs.entityId') || 'Entitás ID')}</th>
                <th>{t('logs.userId') || 'User ID'}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => { setSelected(r); setShowRaw(false); }} title={t('logs.detailsTitle') || 'Details'}>
                  <td>{formatDate(r.created_at)}</td>
                  <td>{renderActionCell(r, t)}</td>
                  <td>{r.entity_type}</td>
                  <td style={{ textAlign: 'center' }}>
                    {activeTab === 'bin' ? (binIdMap.get(r.entity_id) || r.entity_id)
                      : activeTab === 'AppSettings' ? renderSettingName(r, t)
                      : r.entity_id}
                  </td>
                  <td style={{ textAlign: 'center' }}>{r.user_id}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', opacity: .6 }}>{t('logs.empty') || 'Nincs adat'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin && activeTab === 'cmdqueue' && (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Állapot:</span>
            {[['all','Összes'],['pending','Függőben'],['delivered','Kézbesítve'],['expired','Lejárt']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={`btn btn-sm ${cmdQueueStatus === val ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setCmdQueueStatus(val)}
              >{label}</button>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              style={{ marginLeft: 8 }}
              onClick={() => setCmdQueueRevision(r => r + 1)}
              title="Frissítés"
            >↺ Frissítés</button>
          </div>
          {cmdQueueLoading && <div>Betöltés...</div>}
          {cmdQueueError && <div className="alert alert-danger">{cmdQueueError}</div>}
          {!cmdQueueLoading && !cmdQueueError && (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-sm" style={{ minWidth: 900, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>ID</th>
                    <th>Kuka</th>
                    <th>Topic</th>
                    <th style={{ maxWidth: 200 }}>Payload</th>
                    <th style={{ width: 130 }}>Létrehozva</th>
                    <th style={{ width: 60, textAlign: 'center' }}>Kísérlet</th>
                    <th style={{ width: 130 }}>Utolsó kísérlet</th>
                    <th style={{ width: 130 }}>Állapot</th>
                    <th style={{ width: 130 }}>Lejár</th>
                  </tr>
                </thead>
                <tbody>
                  {cmdQueueRows.map(r => {
                    const now = new Date();
                    const isDelivered = !!r.delivered_at;
                    const isExpired   = !r.delivered_at && new Date(r.expires_at) <= now;
                    const isPending   = !isDelivered && !isExpired;
                    const rowStyle = isDelivered
                      ? { background: 'rgba(40,120,40,0.15)' }
                      : isExpired
                        ? { background: 'rgba(180,40,40,0.15)' }
                        : { background: 'rgba(200,140,0,0.10)' };
                    const badge = isDelivered
                      ? <span className="badge badge-success">✓ kézbesítve</span>
                      : isExpired
                        ? <span className="badge badge-danger">✗ lejárt</span>
                        : <span className="badge badge-warning">⏳ függőben</span>;
                    const topicParts = r.topic.split('/');
                    const topicShort = topicParts.slice(-3).join('/');
                    return (
                      <tr key={r.id} style={rowStyle}>
                        <td>{r.id}</td>
                        <td style={{ fontFamily: 'monospace' }}>{r.bin_identifier || r.bin_id}</td>
                        <td title={r.topic} style={{ fontFamily: 'monospace', fontSize: 11 }}>…/{topicShort}</td>
                        <td
                          style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                          title={r.payload}
                        >{r.payload}</td>
                        <td>{formatDate(r.created_at)}</td>
                        <td style={{ textAlign: 'center' }}>{r.attempts}</td>
                        <td>{r.last_attempt ? formatDate(r.last_attempt) : '—'}</td>
                        <td>{badge}{isDelivered ? <span style={{ fontSize: 11, opacity: .7, marginLeft: 4 }}>{formatDate(r.delivered_at)}</span> : null}</td>
                        <td style={{ opacity: isExpired ? 1 : 0.6 }}>{formatDate(r.expires_at)}</td>
                      </tr>
                    );
                  })}
                  {cmdQueueRows.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', opacity: .6 }}>Nincs adat</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {isAdmin && (
        <Modal
          open={!!selected}
          size="lg"
          title={(t('logs.detailsTitle') || 'Részletek') + (selected ? ` – ${selected.action}` : '')}
          onClose={() => setSelected(null)}
          actions={<>
            <button className="btn btn-secondary" onClick={() => setShowRaw(s => !s)}>{showRaw ? (t('logs.hideRaw')||'Hide raw') : (t('logs.showRaw')||'Show raw')}</button>
            <button className="btn btn-primary" onClick={() => setSelected(null)}>{t('logs.close') || 'Close'}</button>
          </>}
        >
          {selected && (
            <div>
              <div style={{ fontSize: 12, opacity: .7, marginBottom: 8 }}>
                ID: {selected.id} • {formatDate(selected.created_at)} • user_id={selected.user_id}
              </div>
              {!showRaw && (
                <div>
                  {/* Special messages for create/delete scenarios */}
                  {(!selected.before || Object.keys(selected.before||{}).length===0) && selected.after && selected.action?.includes('create') && (
                    <div style={{ marginBottom: 8, fontStyle: 'italic', opacity:.8 }}>{t('logs.diffBefore')}: Ø (new entity)</div>
                  )}
                  {selected.before && (!selected.after || Object.keys(selected.after||{}).length===0) && selected.action?.includes('delete') && (
                    <div style={{ marginBottom: 8, fontStyle: 'italic', opacity:.8 }}>{t('logs.diffAfter')}: Ø (deleted)</div>
                  )}
                  {diffInfo?.changes?.length ? (
                    <div style={{ maxHeight: 280, overflow: 'auto' }}>
                      <table className="table table-sm" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ width: 160 }}>{t('logs.diffChanged') || 'Changed fields'}</th>
                            <th style={{ width: '50%' }}>{t('logs.diffBefore') || 'Before'}</th>
                            <th style={{ width: '50%' }}>{t('logs.diffAfter') || 'After'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diffInfo.changes.map(c => (
                            <tr key={c.key}>
                              <td><code>{c.key}</code></td>
                              <td style={{ whiteSpace: 'pre-wrap', background: '#2b2b2b', textAlign: 'center', verticalAlign: 'middle' }}>{formatValue(c.before, true)}</td>
                              <td style={{ whiteSpace: 'pre-wrap', background: '#1d331d', textAlign: 'center', verticalAlign: 'middle' }}>{formatValue(c.after, false)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ opacity: .7 }}>{t('logs.diffNoChanges') || 'No changes'}</div>
                  )}
                </div>
              )}
              {showRaw && (
                <div style={{ display: 'flex', gap: 12, maxHeight: 300 }}>
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <h5 style={{ margin: '4px 0' }}>{t('logs.diffBefore') || 'Before'}</h5>
                    <pre style={preStyle}>{syntaxHighlight(selected.before)}</pre>
                  </div>
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <h5 style={{ margin: '4px 0' }}>{t('logs.diffAfter') || 'After'}</h5>
                    <pre style={preStyle}>{syntaxHighlight(selected.after)}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

// helper to format value succinctly
function formatValue(v, isBefore){
  if (v === null) return 'null';
  if (v === undefined) return isBefore ? '—' : '—';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch(e){ return '[object]'; }
  }
  if (v === '') return '""';
  return String(v);
}

const preStyle = { background: '#111', color: '#eee', padding: 8, fontSize: 12, borderRadius: 4 };

function syntaxHighlight(obj){
  try {
    return JSON.stringify(obj, null, 2);
  } catch(e){
    return String(obj);
  }
}

// Diagnostic: log selection when it changes (can be removed later)
// (Window guard to avoid server side issues in improbable SSR scenario)
if (typeof window !== 'undefined') {
  // naive attach; will run every render but cheap
  if (window.__SB_LAST_SELECTED !== undefined) {
    // do nothing
  }
}

export default LogsPage;

// Map AppSettings changes to friendly setting label(s)
function renderSettingName(row, t){
  const before = row.before || {};
  const after = row.after || {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).filter(k => {
    // only show known setting keys
    return ['battery_low_threshold','fill_green_threshold','fill_yellow_threshold','fill_red_threshold','language'].includes(k);
  });
  const labels = keys.map(k => {
    if (k === 'battery_low_threshold') return t('settings.batteryLowThreshold') || 'Akkumulátor alacsony küszöb (V)';
    if (k === 'fill_green_threshold') return t('settings.fillGreenThreshold') || 'Telítettség zöld küszöb (%)';
    if (k === 'fill_yellow_threshold') return t('settings.fillYellowThreshold') || 'Telítettség sárga küszöb (%)';
    if (k === 'fill_red_threshold') return t('settings.fillRedThreshold') || 'Telítettség piros küszöb (%)';
    if (k === 'language') return t('settings.language') || 'Nyelv';
    return k;
  });
  if (labels.length === 0) return row.entity_id || '—';
  return labels.join(', ');
}

// Render action cell with badge for media.ack events
function renderActionCell(row, t){
  const act = row.action || '';
  if (act.startsWith('media.ack.')){
    const status = (row.after && row.after.status) || '';
    const cls = status === 'ok' ? 'badge badge-success'
      : status === 'fail' ? 'badge badge-danger'
      : 'badge badge-secondary';
    const label = act.replace('media.ack.', '') || t('logs.unknown') || 'unknown';
    return (<span className={cls}>{(t('logs.mediaAck')||'Média ACK')} {label}{status?` (${status})`:''}</span>);
  }
  return (<span>{act}</span>);
}
